/**
 * googleDriveService.js - Google Drive Integration Service
 * Baba AI - OAuth + File Operations
 * 
 * Features:
 * - OAuth 2.0 authentication via Worker proxy
 * - File search, list, read, create
 * - Workspace management (Baba_Workspace)
 * - MCS file operations
 * 
 * Scope: drive.file (only files created by app)
 * 
 * Updated: November 26, 2025
 */

const WORKER_URL = 'https://brave-search-proxy.reza-dizadji.workers.dev';
const CLIENT_ID = '675400760719-9lujlttfijqjduido8c4i2918j2k7i38.apps.googleusercontent.com';
const REDIRECT_URI = window.location.origin + '/oauth/callback';
const SCOPES = 'https://www.googleapis.com/auth/drive.file';

class GoogleDriveService {
  constructor() {
    this.accessToken = localStorage.getItem('gdrive_access_token');
    this.refreshToken = localStorage.getItem('gdrive_refresh_token');
    this.tokenExpiry = localStorage.getItem('gdrive_token_expiry');
    this.workspaceId = localStorage.getItem('baba_workspace_id');
  }

  // ==========================================
  // AUTHENTICATION
  // ==========================================

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    if (!this.accessToken) return false;
    
    // Check if token is expired
    if (this.tokenExpiry && Date.now() > parseInt(this.tokenExpiry)) {
      // Token expired, try to refresh
      if (this.refreshToken) {
        this.refreshAccessToken();
        return true; // Optimistically return true, refresh happens async
      }
      return false;
    }
    
    return true;
  }

  /**
   * Initiate OAuth flow
   */
  initiateAuth() {
    const state = this.generateState();
    localStorage.setItem('oauth_state', state);
    
    // Debug logging
    console.log('[OAuth Debug] Generated state:', state);
    console.log('[OAuth Debug] Stored in localStorage:', localStorage.getItem('oauth_state'));
    
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', state);
    
    window.location.href = authUrl.toString();
  }

  /**
   * Handle OAuth callback
   */
  async handleCallback(code, state) {
    // Verify state
    const savedState = localStorage.getItem('oauth_state');
    
    // Debug logging
    console.log('[OAuth Debug] Received state:', state);
    console.log('[OAuth Debug] Saved state:', savedState);
    console.log('[OAuth Debug] Match:', state === savedState);
    
    if (!savedState) {
      throw new Error('No OAuth state found - did you start the auth flow from this browser?');
    }
    
    if (state !== savedState) {
      throw new Error(`OAuth state mismatch. Expected: ${savedState?.substring(0,8)}... Got: ${state?.substring(0,8)}...`);
    }
    
    localStorage.removeItem('oauth_state');

    // Exchange code for tokens via worker
    const response = await fetch(`${WORKER_URL}/gdrive/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        redirect_uri: REDIRECT_URI
      })
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    const tokens = await response.json();
    this.saveTokens(tokens);
    
    return true;
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${WORKER_URL}/gdrive/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refresh_token: this.refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokens = await response.json();
      this.saveTokens(tokens);
      
      return true;
    } catch (error) {
      console.error('[GDrive] Token refresh failed:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Save tokens to storage
   */
  saveTokens(tokens) {
    this.accessToken = tokens.access_token;
    localStorage.setItem('gdrive_access_token', tokens.access_token);
    
    if (tokens.refresh_token) {
      this.refreshToken = tokens.refresh_token;
      localStorage.setItem('gdrive_refresh_token', tokens.refresh_token);
    }
    
    if (tokens.expires_in) {
      const expiry = Date.now() + (tokens.expires_in * 1000);
      this.tokenExpiry = expiry.toString();
      localStorage.setItem('gdrive_token_expiry', expiry.toString());
    }
  }

  /**
   * Logout - clear all tokens
   */
  logout() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.workspaceId = null;
    
    localStorage.removeItem('gdrive_access_token');
    localStorage.removeItem('gdrive_refresh_token');
    localStorage.removeItem('gdrive_token_expiry');
    localStorage.removeItem('baba_workspace_id');
    localStorage.removeItem('baba_workspace_name');
  }

  /**
   * Generate random state for OAuth
   */
  generateState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  // ==========================================
  // API REQUESTS
  // ==========================================

  /**
   * Make authenticated API request
   */
  async makeRequest(endpoint, options = {}) {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    // Check if token needs refresh
    if (this.tokenExpiry && Date.now() > parseInt(this.tokenExpiry) - 60000) {
      await this.refreshAccessToken();
    }

    const url = endpoint.startsWith('http') 
      ? endpoint 
      : `https://www.googleapis.com/drive/v3${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (response.status === 401) {
      // Token expired, try refresh
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        // Retry request
        return this.makeRequest(endpoint, options);
      }
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API request failed: ${error}`);
    }

    return response.json();
  }

  // ==========================================
  // FILE OPERATIONS
  // ==========================================

  /**
   * Search files by name pattern
   * @param {string} pattern - Search pattern (e.g., 'MCS_v')
   * @param {string} mimeType - Optional MIME type filter
   */
  async searchFiles(pattern, mimeType = null) {
    let query = `name contains '${pattern}' and trashed = false`;
    
    if (mimeType) {
      query += ` and mimeType = '${mimeType}'`;
    }

    const params = new URLSearchParams({
      q: query,
      pageSize: '50',
      fields: 'files(id,name,mimeType,modifiedTime,size,parents)',
      orderBy: 'modifiedTime desc'
    });

    const result = await this.makeRequest(`/files?${params.toString()}`);
    return result.files || [];
  }

  /**
   * List files in folder
   */
  async listFiles(options = {}) {
    const {
      folderId = null,
      query = null,
      pageSize = 50,
      orderBy = 'modifiedTime desc'
    } = options;

    let q = query || '';
    
    if (folderId) {
      q = q ? `${q} and '${folderId}' in parents` : `'${folderId}' in parents`;
    }
    
    q = q ? `${q} and trashed = false` : 'trashed = false';

    const params = new URLSearchParams({
      q,
      pageSize: pageSize.toString(),
      fields: 'nextPageToken,files(id,name,mimeType,modifiedTime,size,parents)',
      orderBy
    });

    return this.makeRequest(`/files?${params.toString()}`);
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(fileId) {
    const params = new URLSearchParams({
      fields: 'id,name,mimeType,modifiedTime,size,parents,description'
    });

    return this.makeRequest(`/files/${fileId}?${params.toString()}`);
  }

  /**
   * Read file content
   */
  async readFile(fileId) {
    const metadata = await this.getFileMetadata(fileId);
    
    // Google Docs need export
    if (metadata.mimeType === 'application/vnd.google-apps.document') {
      return this.exportFile(fileId, 'text/plain');
    }
    
    // Regular files - download directly
    return this.downloadFile(fileId);
  }

  /**
   * Download file content
   */
  async downloadFile(fileId) {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    return response.text();
  }

  /**
   * Export Google Doc as text
   */
  async exportFile(fileId, mimeType = 'text/plain') {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${encodeURIComponent(mimeType)}`,
      {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.text();
  }

  /**
   * Create folder
   */
  async createFolder(name, parentId = null) {
    const metadata = {
      name,
      mimeType: 'application/vnd.google-apps.folder'
    };

    if (parentId) {
      metadata.parents = [parentId];
    }

    return this.makeRequest('/files', {
      method: 'POST',
      body: JSON.stringify(metadata)
    });
  }

  /**
   * Find or create folder
   */
  async findOrCreateFolder(name, parentId = null) {
    // Search for existing folder
    let query = `name = '${name}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
    
    if (parentId) {
      query += ` and '${parentId}' in parents`;
    }

    const params = new URLSearchParams({
      q: query,
      pageSize: '1',
      fields: 'files(id,name)'
    });

    const result = await this.makeRequest(`/files?${params.toString()}`);

    if (result.files && result.files.length > 0) {
      return result.files[0];
    }

    // Create new folder
    return this.createFolder(name, parentId);
  }

  /**
   * Create file
   */
  async createFile(name, content, mimeType = 'text/plain', parentId = null) {
    const metadata = { name };
    
    if (parentId) {
      metadata.parents = [parentId];
    }

    // Use multipart upload
    const boundary = '-------314159265358979323846';
    const delimiter = `\r\n--${boundary}\r\n`;
    const closeDelimiter = `\r\n--${boundary}--`;

    const body = 
      delimiter +
      'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      `Content-Type: ${mimeType}\r\n\r\n` +
      content +
      closeDelimiter;

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': `multipart/related; boundary=${boundary}`
        },
        body
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  // ==========================================
  // WORKSPACE MANAGEMENT
  // ==========================================

  /**
   * Initialize Baba_Workspace (main folder only)
   * Subfolders created on-demand as features are used
   */
  async initializeBabaWorkspace() {
    const workspaceName = localStorage.getItem('baba_workspace_name') || 'Baba_Workspace';
    
    const workspace = await this.findOrCreateFolder(workspaceName);
    
    this.workspaceId = workspace.id;
    localStorage.setItem('baba_workspace_id', workspace.id);
    localStorage.setItem('baba_workspace_name', workspaceName);
    
    console.log(`[GDrive] Workspace initialized: ${workspaceName} (${workspace.id})`);
    return workspace;
  }

  /**
   * Get workspace ID
   */
  async getWorkspaceId() {
    if (this.workspaceId) return this.workspaceId;
    
    const storedId = localStorage.getItem('baba_workspace_id');
    if (storedId) {
      this.workspaceId = storedId;
      return storedId;
    }
    
    // Initialize if not exists
    const workspace = await this.initializeBabaWorkspace();
    return workspace.id;
  }

  /**
   * Archive a chat (creates Archives folder on-demand)
   */
  async archiveChat(chatData, chatName) {
    const workspaceId = await this.getWorkspaceId();
    const archives = await this.findOrCreateFolder('Archives', workspaceId);
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Chat_${timestamp}_${chatName.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    
    const content = JSON.stringify(chatData, null, 2);
    return this.createFile(filename, content, 'application/json', archives.id);
  }

  /**
   * Find latest MCS file
   */
  async findLatestMCS() {
    const files = await this.searchFiles('MCS_v');
    
    if (!files || files.length === 0) {
      return null;
    }

    // Filter for MCS files and sort by version
    const mcsFiles = files.filter(f => f.name.match(/^MCS_v\d+[_\.]\d+/));
    
    if (mcsFiles.length === 0) {
      return null;
    }

    // Sort by version number (extract from filename)
    const sorted = mcsFiles.sort((a, b) => {
      const versionA = this.extractVersion(a.name);
      const versionB = this.extractVersion(b.name);
      return versionB - versionA;
    });

    return sorted[0];
  }

  /**
   * Extract version number from filename
   * MCS_v2_9.md -> 2.9 -> 2009
   * MCS_v2.10.md -> 2.10 -> 2010
   */
  extractVersion(filename) {
    const match = filename.match(/v(\d+)[_\.](\d+)/);
    if (!match) return 0;
    
    const major = parseInt(match[1]);
    const minor = parseInt(match[2]);
    return major * 1000 + minor;
  }
}

// Singleton
const googleDriveService = new GoogleDriveService();

export default googleDriveService;
